import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  FlexColumn,
  FormControls,
  Loading,
  LoadingButton,
  SegmentedButtonGroup,
  SegmentedButton,
  TextField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { ValueLabelOption } from '@elementary/common-ui/types';
import { Autocomplete } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { alertIntegrationLabel } from 'shared/constants';
import {
  IntegrationType,
  AlertDestinationFormData,
  ConfigurationType,
} from 'shared/types';
import { isValidEmail, hasInvalidEmails } from 'shared/utils/validators';
import { radius, space } from '@elementary/common-ui/style/utils';
import AlertDestinationIcon from './AlertDestinationIcon';
import EmailsAutocompleteField from './EmailsAutocompleteField';

const ERROR_LINE_HEIGHT = '14px';

const FormWrapper = styled(FlexColumn)`
  border: 1px solid ${({ theme }) => theme.palette.border.default};
  border-radius: ${radius('lg')};
  gap: ${space('lg')};
  padding: ${space('lg')};
`;

const destinationOptionValue = (
  option: string | ValueLabelOption | ValueLabelOption[] | null | undefined,
): string => {
  if (typeof option === 'string') {
    return option;
  }
  if (Array.isArray(option)) {
    return '';
  }
  return option?.value ?? '';
};

const destinationOptionLabel = (
  option: string | ValueLabelOption | ValueLabelOption[] | null | undefined,
): string => {
  if (typeof option === 'string') {
    return option;
  }
  if (Array.isArray(option)) {
    return '';
  }
  return option?.label ?? '';
};

type Props = {
  alertDestination?: {
    type: IntegrationType;
    channel: ValueLabelOption | ValueLabelOption[];
    configurationType?: ConfigurationType;
    ruleId?: string;
  };
  destinationTypes?: IntegrationType[];
  destinationOptions: Record<
    IntegrationType,
    {
      id: string;
      options: ValueLabelOption[];
      isLoaded: boolean;
    }
  >;
  autoSubmit?: boolean;
  onSubmit: (data: AlertDestinationFormData) => void;
  onClose: () => void;
  isLoading?: boolean;
  error?: boolean;
  providersError?: boolean;
  isLoadingDestinations?: boolean;
};

const AlertDestinationForm = ({
  alertDestination,
  destinationTypes = [],
  destinationOptions,
  autoSubmit,
  onSubmit,
  onClose,
  isLoading,
  error,
  providersError,
  isLoadingDestinations,
}: Props) => {
  const defaultValues: AlertDestinationFormData = alertDestination
    ? alertDestination?.type === 'email'
      ? {
          alertProvider: 'email' as const,
          destination: Array.isArray(alertDestination?.channel)
            ? alertDestination?.channel
            : [alertDestination?.channel],
        }
      : {
          alertProvider: alertDestination?.type,
          destination: Array.isArray(alertDestination?.channel)
            ? alertDestination?.channel[0]
            : alertDestination?.channel,
        }
    : {
        alertProvider: destinationTypes[0],
        destination: undefined,
      };

  const methods = useForm<AlertDestinationFormData>({
    defaultValues,
  });

  const [emailInput, setEmailInput] = useState('');

  const alertProvider = methods.watch('alertProvider');
  const destination = methods.watch('destination');
  const allowFreeSolo = !destinationOptions[alertProvider]?.isLoaded;

  const trimmedEmailInput = emailInput.trim();

  const committedEmails =
    alertProvider === 'email' && Array.isArray(destination) ? destination : [];

  const hasInvalidEmailInput = (): boolean => {
    if (alertProvider !== 'email') return false;

    const pendingInvalid =
      trimmedEmailInput !== '' && !isValidEmail(trimmedEmailInput);
    return hasInvalidEmails(committedEmails) || pendingInvalid;
  };

  const isEmailFormValid =
    (committedEmails.length > 0 || trimmedEmailInput !== '') &&
    !hasInvalidEmailInput();

  const isSubmitDisabled =
    (alertProvider === 'email'
      ? !isEmailFormValid
      : !methods.formState.isValid) || !!isLoadingDestinations;

  const handleSubmit = () => {
    const formData = methods.watch();

    if (formData.alertProvider === 'email') {
      const committed = Array.isArray(formData.destination)
        ? formData.destination
        : [];
      const processedDestinations = committed.map((dest) =>
        typeof dest === 'string' ? { label: dest, value: dest } : dest,
      );

      const pending = emailInput.trim();
      if (
        pending &&
        isValidEmail(pending) &&
        !processedDestinations.some((dest) => dest.value === pending)
      ) {
        processedDestinations.push({ label: pending, value: pending });
      }

      if (processedDestinations.length === 0) return;

      onSubmit({
        alertProvider: 'email',
        destination: processedDestinations,
      });
      setEmailInput('');
      return;
    }

    const destination = formData.destination;
    if (!destination) return;

    const processedDestination = Array.isArray(destination)
      ? destination[0]
      : destination;

    onSubmit({
      alertProvider: formData.alertProvider,
      destination: processedDestination,
    });
  };

  useEffect(() => {
    if (!alertDestination?.channel) {
      methods.setValue('destination', undefined);
    }
    setEmailInput('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertProvider]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!isSubmitDisabled) handleSubmit();
        }}
      >
        <FormWrapper>
          {isLoading || isLoadingDestinations ? (
            <Loading size={32} />
          ) : (
            <>
              <FormControls.Row>
                <FormControls.FormText sx={{ whiteSpace: 'nowrap' }}>
                  Alert provider
                </FormControls.FormText>

                {providersError && (
                  <FormControls.ErrorMessage
                    sx={{
                      position: 'unset !important',
                      whiteSpace: 'pre-wrap !important',
                      lineHeight: `${ERROR_LINE_HEIGHT} !important`,
                    }}
                  >
                    Error loading alert providers. Please verify your{' '}
                    <b>messaging and incident management integrations’ </b>
                    connections and try again.
                  </FormControls.ErrorMessage>
                )}

                <Controller
                  name="alertProvider"
                  control={methods.control}
                  rules={{ required: requiredLabel }}
                  render={({ field }) => (
                    <SegmentedButtonGroup
                      value={field.value}
                      onChange={(_, value) => {
                        if (value != null) {
                          field.onChange(value);
                          methods.setValue('destination', undefined);
                        }
                      }}
                      exclusive
                      size="small"
                    >
                      {destinationTypes.map((destType) => (
                        <SegmentedButton
                          key={destType}
                          title={alertIntegrationLabel[destType]}
                          value={destType}
                          size="small"
                        >
                          <AlertDestinationIcon type={destType} size={18} />
                        </SegmentedButton>
                      ))}
                    </SegmentedButtonGroup>
                  )}
                />
              </FormControls.Row>

              <FormControls.Row>
                <FormControls.FormText
                  sx={{
                    alignSelf: 'start',
                    pt: '10px', // to align with the input field in origin height
                  }}
                >
                  Destination name
                </FormControls.FormText>

                {methods.watch('alertProvider') === 'email' ? (
                  <EmailsAutocompleteField
                    methods={methods}
                    emails={destinationOptions['email']?.options || []}
                    inputValue={emailInput}
                    onInputValueChange={setEmailInput}
                    autoSubmit={autoSubmit}
                    onAutoSubmit={handleSubmit}
                  />
                ) : (
                  <FormControls.InputWrapper style={{ flex: 1 }}>
                    <Controller
                      name="destination"
                      control={methods.control}
                      rules={{ required: requiredLabel }}
                      render={({ field }) => (
                        <Autocomplete
                          loadingText="Loading destinations..."
                          options={destinationOptions[alertProvider]?.options}
                          value={field.value ?? null}
                          getOptionLabel={destinationOptionLabel}
                          isOptionEqualToValue={(option, value) =>
                            destinationOptionValue(option) ===
                            destinationOptionValue(value)
                          }
                          renderOption={(props, option) => (
                            <li {...props} key={destinationOptionValue(option)}>
                              {destinationOptionLabel(option)}
                            </li>
                          )}
                          freeSolo={allowFreeSolo}
                          disabled={!methods.watch('alertProvider')}
                          onChange={(_, val) => {
                            // When selecting from dropdown, val is an object
                            // When clearing, val is null
                            // In freeSolo mode when pressing Enter, val can be a string
                            if (typeof val === 'string') {
                              field.onChange({ label: val, value: val });
                            } else {
                              field.onChange(val);
                            }
                            if (autoSubmit) {
                              handleSubmit();
                            }
                          }}
                          onInputChange={(_, inputValue, reason) => {
                            // In freeSolo mode, update form value as user types
                            // Only update on 'input' reason (user typing), not 'reset' or 'clear'
                            if (allowFreeSolo && reason === 'input') {
                              field.onChange({
                                label: inputValue,
                                value: inputValue,
                              });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.stopPropagation();
                              if (field.value) {
                                handleSubmit();
                              }
                            }
                          }}
                          onBlur={() => {
                            field.onBlur();
                            if (autoSubmit) {
                              handleSubmit();
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="large"
                              placeholder={
                                allowFreeSolo
                                  ? 'Enter destination'
                                  : 'Choose destination'
                              }
                            />
                          )}
                        />
                      )}
                    />
                  </FormControls.InputWrapper>
                )}
              </FormControls.Row>

              <FormControls.InputWrapper>
                {error ? (
                  <FormControls.ErrorMessage>
                    Something went wrong. Please try again.
                  </FormControls.ErrorMessage>
                ) : null}
              </FormControls.InputWrapper>

              {!autoSubmit && (
                <FormControls.Row style={{ justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      onClose();
                      methods.reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    disabled={isSubmitDisabled}
                    onClick={handleSubmit}
                    loading={isLoading}
                  >
                    {alertDestination ? 'Save' : 'Add'}
                  </LoadingButton>
                </FormControls.Row>
              )}
            </>
          )}
        </FormWrapper>
      </form>
    </FormProvider>
  );
};

export default AlertDestinationForm;
